// Utils
// Types
import type { DateRange } from 'react-day-picker';

import { DATETIME_FORMAT_SCHEMAS } from '@/utils/hooks/format/useFormatDateTime';
import useUser from '@/utils/hooks/swr/useUser';

// Lib
import { dayOfWeekNumber } from '@/lib/date/dayOfWeek';
import type { DatepickerPeriodsAvailableType } from '@/lib/periods';

// Components
import { DatepickerWrapper } from '@/components/base/Datepicker/DatepickerComponent';
import type { DatepickerWrapperProps } from '@/components/base/Datepicker/DatepickerComponent/components/DatepickerWrapper';
import { CompareToTab } from '@/components/composite/PeriodPicker/components/CompareToTab';
import { PeriodTabs } from '@/components/composite/PeriodPicker/components/PeriodTabs';

type PeriodPickerPeriodType = {
    date: DateRange;
    period: DatepickerPeriodsAvailableType;
};

export type PeriodPickerProps = { children: React.ReactNode } & PeriodPickerPeriodType &
    Pick<DatepickerWrapperProps['settings'], 'minDate' | 'maxDate'> & {
        onChange: (arg: PeriodPickerPeriodType) => void;
    };

function PeriodPicker({ date, period, minDate, maxDate, onChange, children }: PeriodPickerProps) {
    const { user } = useUser();

    const settings = {
        timezone: user.settings.timezone,
        dateFormat: DATETIME_FORMAT_SCHEMAS[user.settings.date_format],
        lang: user.settings.platform_language,
        minDate,
        maxDate
    };

    return (
        <DatepickerWrapper
            date={date}
            period={period}
            mode="range"
            onChange={onChange}
            settings={settings}
            dayPickerOptions={{
                weekStartsOn: dayOfWeekNumber[user.settings.first_day_week]
            }}>
            <div className="relative z-15 flex max-w-fit flex-wrap items-center gap-x-1 gap-y-2">
                {children}
            </div>
        </DatepickerWrapper>
    );
}

export { CompareToTab, PeriodPicker, PeriodTabs };
